import { Popover as BasePopover } from '@base-ui/react/popover';
import { merge } from 'lodash-es';
import React from 'react';

import type { GutterProp } from '../../utilities/shared/Gutter';
import type { MaxWidthProps } from '../../utilities/shared/MaxWidth';
import type { MinWidthProps } from '../../utilities/shared/MinWidth';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { WidthProps } from '../../utilities/shared/Width';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';

export const Popover = styled(BasePopover.Root);
Popover.displayName = 'Popover';

export const PopoverTrigger = styled(BasePopover.Trigger, {
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
});
PopoverTrigger.displayName = 'PopoverTrigger';

const PopoverPopover = styled(BasePopover.Popup, {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  background: colors.bgApplicationLight,
  borderRadius: '$10',
  boxShadow: shadows.overlayLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  '@media (prefers-reduced-motion: no-preference)': {
    transition: 'opacity 150ms ease-out, transform 150ms ease-out',

    '&[data-starting-style]': {
      opacity: 0,
      transform: 'translateY(4px) scale(0.98)',
    },

    '&[data-ending-style]': {
      opacity: 0,
      transform: 'translateY(4px) scale(0.98)',
    },
  },
});
PopoverPopover.displayName = 'PopoverPopover';

const PopoverPositioner = styled(BasePopover.Positioner, {
  zIndex: zIndexes.overlay,
});
PopoverPositioner.displayName = 'PopoverPositioner';

const PopoverPortal = styled(BasePopover.Portal);
PopoverPortal.displayName = 'PopoverPortal';

export type PopoverContentProps = {
  children: React.ReactNode;
  alignment?: 'start' | 'center' | 'end';
  collisionPadding?: SpacingScale;
  sideOffset?: SpacingScale;
  side?: 'top' | 'right' | 'bottom' | 'left';
} & GutterProp &
  WidthProps &
  MaxWidthProps &
  MinWidthProps;

export function PopoverContent({
  children,
  alignment = 'center',
  collisionPadding = 12,
  gutter = { context: 'content', edge: 'all' },
  side = 'bottom',
  sideOffset = 8,
  width,
  maxWidth = 360,
  minWidth = 240,
}: PopoverContentProps) {
  const popoverPopupCSS = merge(
    {
      maxWidth: 'var(--available-width)',
      maxHeight: 'var(--available-height)',
    },
    gutterCSS(gutter),
    widthCSS(width),
    maxWidth ? maxWidthCSS(maxWidth) : undefined,
    minWidthCSS(minWidth),
  );

  return (
    <PopoverPortal>
      <PopoverPositioner
        side={side}
        sideOffset={sideOffset}
        align={alignment}
        collisionPadding={collisionPadding}
      >
        <PopoverPopover css={popoverPopupCSS}>{children}</PopoverPopover>
      </PopoverPositioner>
    </PopoverPortal>
  );
}
